import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getConfig } from '../../hooks/useAI';
import { apiUrl } from '../../lib/api-url';

export default function AIOptimizerPage() {
  const [campaignId, setCampaignId] = useState('');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'campaign' | 'segment' | 'sendtime'>('campaign');

  const { data: config } = useQuery({ queryKey: ['ai-config'], queryFn: getConfig });

  const handleOptimizeCampaign = async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(apiUrl('/api/v1/ai/optimize/campaign'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ campaign_id: campaignId }),
      });
      const json = await res.json();
      if (json.success) setResult(json.data);
    } catch {
      // error
    } finally {
      setLoading(false);
    }
  };

  const handleOptimizeSegment = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(apiUrl('/api/v1/ai/optimize/segment'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const json = await res.json();
      if (json.success) setResult(json.data);
    } catch {
      // error
    } finally {
      setLoading(false);
    }
  };

  const handleSendTime = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(apiUrl('/api/v1/ai/optimize/send-time'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success) setResult(json.data);
    } catch {
      // error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">AI Optimization Engine</h2>
        <p className="text-gray-500 mt-1">Analyze campaigns, suggest segments, and find optimal send times.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { key: 'campaign', label: 'Campaign' },
          { key: 'segment', label: 'Segments' },
          { key: 'sendtime', label: 'Send Time' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key as any); setResult(null); }}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === tab.key
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Campaign Optimization */}
      {activeTab === 'campaign' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Optimize Campaign</h3>
          <div className="flex gap-3">
            <input
              type="text"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Campaign ID"
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
            />
            <button
              onClick={handleOptimizeCampaign}
              disabled={loading || !campaignId}
              className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Analyzing...' : 'Analyze'}
            </button>
          </div>

          {result?.recommendations && (
            <div className="mt-6 space-y-3">
              {result.recommendations.map((rec: any, i: number) => (
                <div
                  key={i}
                  className={`p-4 rounded-lg border ${
                    rec.type === 'critical' ? 'bg-red-50 border-red-200' :
                    rec.type === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                    rec.type === 'success' ? 'bg-green-50 border-green-200' :
                    'bg-blue-50 border-blue-200'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <span className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                      rec.type === 'critical' ? 'bg-red-500' :
                      rec.type === 'warning' ? 'bg-yellow-500' :
                      rec.type === 'success' ? 'bg-green-500' :
                      'bg-blue-500'
                    }`} />
                    <div>
                      <p className="text-sm text-gray-900">{rec.message}</p>
                      {rec.suggestion && <p className="text-xs text-gray-500 mt-1">{rec.suggestion}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Segment Optimization */}
      {activeTab === 'segment' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Segment Suggestions</h3>
          <button
            onClick={handleOptimizeSegment}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Analyzing...' : 'Suggest Segments'}
          </button>

          {result?.segments && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {result.segments.map((seg: any, i: number) => (
                <div key={i} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900">{seg.name}</h4>
                  <p className="text-sm text-gray-500 mt-1">{seg.description}</p>
                  <p className="text-xs text-gray-400 mt-2">Est. size: ~{seg.estimated_size} contacts</p>
                  <p className="text-xs text-blue-600 mt-2">{seg.strategy}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Send Time */}
      {activeTab === 'sendtime' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Optimal Send Time</h3>
          <button
            onClick={handleSendTime}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Calculating...' : 'Get Recommendation'}
          </button>

          {result && (
            <div className="mt-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="text-center p-6 bg-blue-50 rounded-xl">
                  <p className="text-3xl font-bold text-blue-600">{result.best_day}</p>
                  <p className="text-sm text-gray-500 mt-1">{result.best_hour}:00 {result.timezone}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Confidence: {Math.round((result.confidence || 0) * 100)}%</p>
                  <p className="text-sm text-gray-500 mt-2">{result.reasoning}</p>
                </div>
              </div>

              {result.alternatives && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Alternative Times</h4>
                  <div className="space-y-2">
                    {result.alternatives.map((alt: any, i: number) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-900">{alt.day} at {alt.hour}:00</span>
                        <span className="text-xs text-gray-500">{Math.round((alt.confidence || 0) * 100)}% confidence</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
